import { useEffect, useRef, useCallback } from 'react';

/**
 * Хук для общения с родительским окном через postMessage API
 * Отправляет размер страницы родительскому окну для автоматической настройки высоты iframe
 */
export const useIframeCommunication = () => {
    const timeoutRef = useRef(null);
    const lastHeightRef = useRef(0);

    // Функция для отправки размера страницы родительскому окну
    const sendPageHeight = useCallback(() => {
        // Проверяем, что мы находимся в iframe
        if (window.self !== window.top) {
            const height = Math.max(
                document.body.scrollHeight,
                document.body.offsetHeight,
                document.documentElement.clientHeight,
                document.documentElement.scrollHeight,
                document.documentElement.offsetHeight
            );

            // Отправляем только если высота изменилась (оптимизация)
            if (height !== lastHeightRef.current) {
                lastHeightRef.current = height;
                // Отправляем сообщение в формате "calendar {height}px"
                window.parent.postMessage(`calendar ${height}px`, '*');
            }
        }
    }, []);

    // Отправка размера при загрузке и изменении размера
    useEffect(() => {
        // Отправляем размер сразу после монтирования
        sendPageHeight();

        // Создаем ResizeObserver для отслеживания изменений размера контента
        const resizeObserver = new ResizeObserver(() => {
            // Используем debounce для оптимизации
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                sendPageHeight();
            }, 100);
        });

        // Наблюдаем за изменениями размера body и documentElement
        resizeObserver.observe(document.body);
        resizeObserver.observe(document.documentElement);

        // Также слушаем события изменения размера окна
        const handleResize = () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                sendPageHeight();
            }, 100);
        };

        window.addEventListener('resize', handleResize);

        // Слушаем сообщения от родительского окна
        const handleMessage = (event) => {
            // Проверяем происхождение сообщения для безопасности
            // Разрешаем сообщения от любых источников (для работы в iframe)
            // В продакшене можно добавить проверку:
            // const allowedOrigins = ['https://psy.nasledietz.ru', 'https://nasledietz.ru'];
            // if (!allowedOrigins.some(origin => event.origin.startsWith(origin))) return;

            if (typeof event.data === 'string') {
                // Обрабатываем команду scrollToTable
                if (event.data === 'scrollToTable') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }

                // Обрабатываем команду resize для принудительного обновления размера
                if (event.data === 'resize') {
                    sendPageHeight();
                }
            }
        };

        window.addEventListener('message', handleMessage);

        // Периодическая проверка размера (на случай, если ResizeObserver не сработал)
        const intervalId = setInterval(() => {
            sendPageHeight();
        }, 1000);

        // Очистка при размонтировании
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('message', handleMessage);
            clearInterval(intervalId);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [sendPageHeight]);

    // Отправляем размер при изменении контента (например, после загрузки данных)
    useEffect(() => {
        // Небольшая задержка для того, чтобы DOM успел обновиться
        const timer = setTimeout(() => {
            sendPageHeight();
        }, 300);

        return () => clearTimeout(timer);
    }, [sendPageHeight]);
};

